import "./style.scss";
import { FC, useEffect, useMemo, useState } from "react";
import { IApplyInfoComment } from "@/src/types/applicationOnline";
import { commentsRequests } from "@/src/api/commentsRequests";
import UserSvg from "@/src/svg/header/UserSvg";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import DotsSvg from "@/src/svg/applicationsPage/APPopUp/Documents/DotsSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import SendSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/SendSvg";
import SortSvg from "@/src/svg/applicationsPage/APPopUp/Comments/SortSvg";
import CapitalizeSvg from "@/src/svg/applicationsPage/APPopUp/Comments/CapitalizeSvg";
import ClipSvg from "@/src/svg/applicationsPage/APPopUp/Comments/СlipSvg";
import ProfileSvg from "@/src/svg/CustomUtils/ProfileSvg";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";

interface CommentsProps {
  comments: IApplyInfoComment[];
  applicantId: number | undefined;
  onRefreshUserInfo?: () => Promise<void>;
}

const Comments: FC<CommentsProps> = ({ comments, applicantId, onRefreshUserInfo }) => {
  console.log("This is applicantId:", applicantId);
  console.log("This is comments:", comments);
  const formatDateDisplay = useFormatDateDisplay();
  const [draft, setDraft] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [openCommentMenuId, setOpenCommentMenuId] = useState<number | null>(null);
  const [isReversed, setIsReversed] = useState(true);

  const displayedComments = useMemo(() => (isReversed ? [...comments].reverse() : comments), [comments, isReversed]);

  useEffect(() => {
    if (openCommentMenuId == null) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".comments-list-item-header-date-dots-button")) return;
      setOpenCommentMenuId(null);
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [openCommentMenuId]);

  const handleAddComment = async () => {
    const text = draft.trim();
    if (!text || applicantId == null || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await commentsRequests.createApplicantComment({
        text,
        applicant_id: applicantId,
      });
      if (result == null) return;
      setDraft("");
      setIsEditorOpen(false);
      await onRefreshUserInfo?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number | undefined) => {
    if (commentId == null || applicantId == null || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await commentsRequests.deleteApplicantComment(commentId);
      if (result == null) return;
      setOpenCommentMenuId(null);
      await onRefreshUserInfo?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="comments">
      <>
        {!isEditorOpen && (
          <div className="comments-buttons">
            <BtnDefault onClick={() => setIsEditorOpen(true)} reverse={true}>
              <p>Send Comment</p>
              <SendSvg />
            </BtnDefault>
            <BtnDefault onClick={() => setIsReversed((prev) => !prev)}>
              <p>Sort by</p>
              <SortSvg />
            </BtnDefault>
          </div>
        )}
        {isEditorOpen && (
          <div className="comments-editor">
            <textarea placeholder="Comment..." value={draft} onChange={(e) => setDraft(e.target.value)} />
            <div className="comments-editor-info">
              <div className="comments-editor-info-buttons">
                <BtnDefault>
                  <CapitalizeSvg />
                </BtnDefault>
                <BtnDefault>
                  <ClipSvg />
                </BtnDefault>
                <BtnDefault>
                  <ProfileSvg />
                </BtnDefault>
              </div>
              <VerticalLine />
              <div className="comments-editor-info-statistics">
                <p>0 people will see the comment</p>
              </div>
              <BtnDefault onClick={handleAddComment} reverse={true}>
                <p>Send</p>
                <SendSvg />
              </BtnDefault>
            </div>
          </div>
        )}
      </>
      {comments.length === 0 ? (
        <div className="comments-empty">
          <p>No comments yet</p>
        </div>
      ) : (
        <div className="comments-list">
          {displayedComments.map((item) => (
            <div key={`${item.id}`} className="comments-list-item">
              <div className="comments-list-item-logo">
                <UserSvg />
              </div>
              <div className="comments-list-item-container">
                <div className="comments-list-item-header">
                  <div className="comments-list-item-header-title">
                    <p>{item.commentator ?? "commentator"}</p>
                    <VerticalLine />
                    <span>{item.commentator_role ?? "role"}</span>
                  </div>
                  <div className="comments-list-item-header-date">
                    <p>{formatDateDisplay(item.created_at)}</p>
                    <div className="comments-list-item-header-date-dots">
                      <div
                        className="comments-list-item-header-date-dots-button"
                        onClick={() => setOpenCommentMenuId((prev) => (prev === item.id ? null : (item.id ?? null)))}
                      >
                        <DotsSvg />
                      </div>
                      {openCommentMenuId === item.id && (
                        <div className="comments-list-item-header-date-dots-popUp">
                          <div
                            className="comments-list-item-header-date-dots-popUp-item delete"
                            onClick={() => handleDeleteComment(item.id)}
                          >
                            <p>Delete</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="comments-list-item-body">
                  <p>{item.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Comments;
